import { OverlayModule, Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';

import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Editor } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { TiptapEditorDirective } from 'ngx-tiptap';

import { MdwAvatar } from '@spaces-ui/meadow/components/avatar';
import { MdwTooltipIfClipped } from '@spaces-ui/meadow/components/tooltip';
import { QuillMentionMatch } from '@spaces-ui/meadow/helpers/quill-helpers';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import { alphaSortByProperty } from '@spaces-ui/utils';

import { ChipNodeExtension } from './chip-node-extension';

@Component({
  selector: 'mdw-tiptap-chip-list-input',
  templateUrl: './tiptap-chip-list-input.html',
  styleUrls: ['./tiptap-chip-list-input.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TiptapEditorDirective,
    MdwAvatar,
    MdwIcon,
    ScrollingModule,
    OverlayModule,
    MdwTooltipIfClipped,
    MatTooltipModule,
  ],
})
export class MdwTiptapChipListInput<T> implements OnDestroy {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  public items = input.required<T[]>();
  public placeholder = input<string>('Search items');
  public selectedItems = model.required<T[]>();
  public toMentionMatch = input.required<(item: T) => QuillMentionMatch>();
  public fromMentionMatch = input.required<(mentionMatch: QuillMentionMatch) => T | undefined>();
  public shouldAutoFocus = input<boolean>(false);
  /** Shows the chips without letting them be added, removed or typed around. */
  public readOnly = input<boolean>(false);
  public maxItems = input<number>();
  public maxItemsErrorMessage = input<string>('Too many items selected');
  public searchProperties = input<Array<keyof T>>([]);
  public sortBy = input<keyof T>();

  public tiptapEditorEl = viewChild<ElementRef<HTMLElement>>('tiptapEditorEl');
  public suggestionMenuTemplate = viewChild('suggestionMenu', {
    read: TemplateRef,
  });

  public submitChips = output<void>();

  public hasError = computed(() => {
    const maxItems = this.maxItems();
    const selectedItems = this.selectedItems();

    return maxItems !== undefined && selectedItems.length > maxItems;
  });

  public isFocussed = signal<boolean>(false);
  public focusedSuggestionIndex = signal<number>(-1);
  public suggestions = signal<QuillMentionMatch[]>([]);

  public tiptapEditor: Editor;

  private overlayRef: OverlayRef | null = null;
  private ignoreNextUpdate = false;

  public constructor() {
    this.tiptapEditor = new Editor({
      extensions: [
        StarterKit.configure({
          bold: false,
          italic: false,
          strike: false,
          code: false,
          codeBlock: false,
          blockquote: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          heading: false,
          horizontalRule: false,
          hardBreak: false,
        }),
        ChipNodeExtension,
        Placeholder.configure({
          placeholder: () => this.placeholder(),
        }),
      ],
      editorProps: {
        handleKeyDown: (_view, event) => {
          return this.handleKeyDown(event);
        },
      },
      onFocus: () => {
        this.isFocussed.set(true);
      },
      onBlur: () => {
        this.isFocussed.set(false);
        this.clearChipFocus();

        // Delay closing to allow clicks to register
        setTimeout(() => {
          if (!this.isFocussed()) {
            this.closeSuggestionMenu();
          }
        }, 100);
      },
      onUpdate: () => {
        if (this.ignoreNextUpdate) {
          this.ignoreNextUpdate = false;
          return;
        }

        this.syncSelectedItems();
        setTimeout(() => this.handleUserTyping());
      },
      onSelectionUpdate: () => {
        this.checkIfCursorInsideChip();
      },
    });

    effect(() => {
      const index = this.focusedSuggestionIndex();
      if (index < 0) {
        return;
      }

      const el = document.getElementById(`suggestion-${index}`);
      if (el) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });

    effect(() => {
      const shouldAutoFocus = this.shouldAutoFocus();
      if (shouldAutoFocus) {
        setTimeout(() => this.setFocus());
      }
    });

    // Matches are only recomputed from `onUpdate`, i.e. on an editor change. A host
    // that loads `items` asynchronously (e.g. a dialog that lazy-loads its users)
    // can populate them *after* the user has finished typing, and nothing re-runs
    // the match — the menu stays empty until the next keystroke. Re-match when the
    // item set changes while the editor is focused. `handleUserTyping` closes the
    // menu when there is no pending search text, so this never opens it unbidden.
    effect(() => {
      this.items();

      if (!untracked(() => this.isFocussed())) {
        return;
      }

      untracked(() => this.handleUserTyping());
    });

    effect(() => {
      const editable = !this.readOnly();

      untracked(() => {
        this.tiptapEditor.setEditable(editable, false);

        if (!editable) {
          this.closeSuggestionMenu();
        }
      });
    });

    // Edits flow editor → `selectedItems` (see `syncSelectedItems`). This is the other direction:
    // chips the host sets (e.g. an edit dialog's saved selection) or re-maps (richer icons once
    // its options load). A value that came from the editor already matches, so it's a no-op.
    effect(() => {
      const toMentionMatch = this.toMentionMatch();
      const matches = this.selectedItems().map(item => toMentionMatch(item));

      untracked(() => this.renderChips(matches));
    });
  }

  public ngOnDestroy() {
    this.closeSuggestionMenu();
    this.tiptapEditor.destroy();
  }

  public trackSuggestion(index: number, item: QuillMentionMatch) {
    return item.id;
  }

  public setFocus() {
    this.tiptapEditor.commands.focus('end');
  }

  public selectSuggestion(item: QuillMentionMatch) {
    const { from } = this.tiptapEditor.state.selection;
    const deleteFrom = this.findLastChipOrStart(from);
    const deleteTo = from;

    const chain = this.tiptapEditor.chain().focus();

    // Delete search text between last chip and cursor
    if (deleteTo > deleteFrom) {
      chain.deleteRange({ from: deleteFrom, to: deleteTo });
    }

    // Insert chip node + zero-width space
    chain
      .insertContentAt(deleteFrom, [
        { type: 'chip', attrs: item },
        { type: 'text', text: '\u200B' },
      ])
      .run();

    this.closeSuggestionMenu();

    setTimeout(() => {
      this.tiptapEditor.commands.focus();
    }, 0);
  }

  private syncSelectedItems() {
    const json = this.tiptapEditor.getJSON();
    const chips: T[] = [];
    const fromMapperFunc = this.fromMentionMatch();

    this.walkContent(json, node => {
      if (node['type'] === 'chip' && node['attrs']) {
        const item = fromMapperFunc(node['attrs'] as QuillMentionMatch);
        if (item) {
          chips.push(item);
        }
      }
    });

    this.selectedItems.set(chips);
  }

  private renderChips(matches: QuillMentionMatch[]) {
    const json = this.tiptapEditor.getJSON();
    const currentChips: Record<string, unknown>[] = [];
    let pendingText = '';

    this.walkContent(json, node => {
      if (node['type'] === 'chip' && node['attrs']) {
        currentChips.push(node['attrs'] as Record<string, unknown>);
      } else if (node['type'] === 'text') {
        pendingText += `${node['text'] ?? ''}`;
      }
    });

    if (sameChips(currentChips, matches)) {
      return;
    }

    // Keep any search text the user has typed; it always trails the chips.
    pendingText = pendingText.replace(/\u200B/g, '');
    const content = [
      ...matches.flatMap(match => [
        { type: 'chip', attrs: match },
        { type: 'text', text: '\u200B' },
      ]),
      ...(pendingText ? [{ type: 'text', text: pendingText }] : []),
    ];

    this.tiptapEditor.commands.setContent(
      { type: 'doc', content: [{ type: 'paragraph', content }] },
      { emitUpdate: false },
    );

    if (this.isFocussed()) {
      this.tiptapEditor.commands.focus('end');
    }
  }

  private walkContent(
    node: Record<string, unknown>,
    callback: (node: Record<string, unknown>) => void,
  ) {
    callback(node);
    const content = node['content'] as Record<string, unknown>[] | undefined;
    if (content) {
      for (const child of content) {
        this.walkContent(child, callback);
      }
    }
  }

  private checkIfCursorInsideChip() {
    this.clearChipFocus();

    const { from } = this.tiptapEditor.state.selection;
    const $pos = this.tiptapEditor.state.doc.resolve(from);
    const nodeBefore = $pos.nodeBefore;

    if (nodeBefore?.type.name === 'chip') {
      const dom = this.tiptapEditor.view.nodeDOM(from - nodeBefore.nodeSize);
      if (dom instanceof HTMLElement) {
        dom.classList.add('focused');
      }
    }
  }

  private clearChipFocus() {
    const editorEl = this.tiptapEditorEl()?.nativeElement;
    if (!editorEl) {
      return;
    }

    const focusedChip = editorEl.querySelector('.mdw-quill-chip.focused');
    focusedChip?.classList.remove('focused');
  }

  private findLastChipOrStart(cursorPos: number): number {
    const doc = this.tiptapEditor.state.doc;
    let lastChipEnd = 1; // default: start of paragraph content (pos 1 = inside the <p>)

    doc.descendants((node, pos) => {
      // Skip nodes that start at or past the cursor
      if (pos >= cursorPos) {
        return false;
      }

      if (node.type.name === 'chip') {
        const nodeEnd = pos + node.nodeSize;
        if (nodeEnd <= cursorPos) {
          lastChipEnd = nodeEnd;
        }
      }

      return true; // always descend into container nodes (e.g. paragraph)
    });

    return lastChipEnd;
  }

  private getSearchTextSinceLastChip(cursorPos: number): string {
    const lastChipEnd = this.findLastChipOrStart(cursorPos);
    const doc = this.tiptapEditor.state.doc;

    return doc
      .textBetween(lastChipEnd, cursorPos, '')
      .replace(/\u200B/g, '')
      .trim();
  }

  private handleKeyDown(event: KeyboardEvent): boolean {
    const isSpace = event.key === ' ' || event.code === 'Space';
    const suggestions = this.suggestions();
    const selectedItems = this.selectedItems();

    if (!suggestions.length && !isSpace && selectedItems.length > 0) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submitChips.emit();
        this.closeSuggestionMenu();
        return true;
      }
      return false;
    }

    if (!suggestions.length && !isSpace) {
      return false;
    }

    const focusedSuggestion = suggestions[this.focusedSuggestionIndex()];

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedSuggestionIndex.set((this.focusedSuggestionIndex() + 1) % suggestions.length);
        return true;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedSuggestionIndex.set(
          (this.focusedSuggestionIndex() - 1 + suggestions.length) % suggestions.length,
        );
        return true;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        if (focusedSuggestion) {
          this.selectSuggestion(focusedSuggestion);
        }
        return true;
      case 'Escape':
        event.preventDefault();
        this.closeSuggestionMenu();
        return true;
      case ' ': {
        const { from } = this.tiptapEditor.state.selection;
        const searchText = this.getSearchTextSinceLastChip(from);

        if (searchText.trim().length === 0) {
          this.ignoreNextUpdate = true;
          setTimeout(() => {
            this.handleUserTyping(true);
          }, 0);
        }
        return false;
      }
      default:
        return false;
    }
  }

  private handleUserTyping(forceShowAll = false) {
    const tiptapEditorEl = this.tiptapEditorEl();
    const suggestionMenuTemplate = this.suggestionMenuTemplate();
    if (!tiptapEditorEl || !suggestionMenuTemplate) {
      return;
    }

    const { from } = this.tiptapEditor.state.selection;

    if (from <= 1) {
      this.closeSuggestionMenu();
      return;
    }

    const searchText = this.getSearchTextSinceLastChip(from);

    const allItems = this.items();
    const selectedItems = this.selectedItems();
    const selectedIds = new Set(selectedItems.map(item => this.toMentionMatch()(item).id));

    let matches: T[] = [];

    if (forceShowAll) {
      matches = allItems.filter(item => {
        const mentionMatch = this.toMentionMatch()(item);
        return !selectedIds.has(mentionMatch.id);
      });
    } else if (searchText.length > 0) {
      const searchWords = searchText
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word.length > 0);
      const searchableProperties = this.searchProperties() ?? [];

      matches = allItems.filter(item => {
        const mentionMatch = this.toMentionMatch()(item);
        let itemText = '';

        if (searchableProperties.length > 0) {
          const searchValues: string[] = [];
          searchableProperties.forEach(prop => {
            if (item[prop] !== undefined) {
              searchValues.push(`${item[prop]}`);
            }
          });
          itemText = searchValues.join(' ').toLowerCase();
        } else {
          itemText = JSON.stringify(item).toLowerCase();
        }

        return (
          !selectedIds.has(mentionMatch.id) && searchWords.every(word => itemText.includes(word))
        );
      });
    } else {
      this.closeSuggestionMenu();
      return;
    }

    if (matches.length > 0) {
      const sortProperty = this.sortBy();
      if (sortProperty) {
        matches = alphaSortByProperty(matches, sortProperty, false);
      }
      this.focusedSuggestionIndex.set(0);
      this.suggestions.set(matches.map(m => this.toMentionMatch()(m)));

      if (!this.overlayRef) {
        const coords = this.tiptapEditor.view.coordsAtPos(from);
        const editorEl = tiptapEditorEl.nativeElement.querySelector('.ProseMirror');

        if (!editorEl || !coords) {
          return;
        }

        const editorRect = editorEl.getBoundingClientRect();

        const positions: ConnectedPosition[] = [
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: coords.left - editorRect.left,
            offsetY: coords.bottom - editorRect.top,
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetX: coords.left - editorRect.left,
            offsetY: -(coords.bottom - coords.top + 8),
          },
        ];

        const positionStrategy = this.overlay
          .position()
          .flexibleConnectedTo(editorEl)
          .withPositions(positions)
          .withFlexibleDimensions(false)
          .withPush(false);

        this.overlayRef = this.overlay.create({
          positionStrategy,
          scrollStrategy: this.overlay.scrollStrategies.reposition(),
          hasBackdrop: false,
          panelClass: 'chip-list-overlay-panel',
        });

        const portal = new TemplatePortal(suggestionMenuTemplate, this.viewContainerRef);
        this.overlayRef.attach(portal);
      }
    } else {
      this.closeSuggestionMenu();
    }
  }

  private closeSuggestionMenu() {
    this.suggestions.set([]);
    this.focusedSuggestionIndex.set(-1);

    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}

/**
 * Chip attrs vs mention matches. Only the chip node's own attributes count (it drops any other
 * match fields), and unset equals null since the node fills in defaults.
 */
function sameChips(chips: Record<string, unknown>[], matches: QuillMentionMatch[]) {
  if (chips.length !== matches.length) {
    return false;
  }

  return chips.every((chip, index) => {
    const match = matches[index] as unknown as Record<string, unknown>;

    return Object.keys(chip).every(key => (chip[key] ?? null) === (match[key] ?? null));
  });
}
